<template>
  <div class="cell-content" :class="{ 'cell-content-inline-action': isInlineActionCell }" :style="cellStyle">
    <img
      v-if="leadingTreeIconUrl"
      :src="leadingTreeIconUrl"
      alt=""
      class="tree-row-leading-icon tree-row-leading-icon-image"
      aria-hidden="true"
    />
    <i v-else-if="leadingTreeIconClass" :class="leadingTreeIconClass" class="tree-row-leading-icon" aria-hidden="true" />
    <template v-if="item[column.value]?.component">
      <component :is="item[column.value].component" readonly :model-value="item[column.value].value" />
    </template>

    <template v-else-if="isInlineActionCell">
      <button
        type="button"
        class="inline-action-button"
        :title="inlineActionTitle"
        @click.stop="handleInlineActionClick"
        @dblclick.stop
      >
        <img
          v-if="inlineActionIconUrl"
          :src="inlineActionIconUrl"
          :alt="inlineActionTitle"
          class="inline-action-icon-img"
          @error="handleInlineActionIconError"
        />
        <i
          v-else-if="inlineActionIconClass"
          :class="inlineActionIconClass"
          class="inline-action-icon"
        />
        <span class="inline-action-label">{{ inlineActionTitle }}</span>
      </button>
    </template>

    <template v-else-if="isListboxIconCell">
      <span
        class="listbox-icon-cell"
        :class="{ 'listbox-icon-cell--large': listboxIconUsesLargeLayout }"
        :title="listboxIconTooltip || visibleListboxDisplayText || undefined"
      >
        <img
          v-if="listboxIconImageUrl"
          :src="listboxIconImageUrl"
          :alt="listboxIconTooltip || visibleListboxDisplayText || ''"
          :class="listboxIconImageClass"
          :style="listboxIconImageStyle"
          @error="handleIconError($event, listboxIconImage)"
        />
        <span v-if="visibleListboxDisplayText" class="listbox-cell-text">{{ visibleListboxDisplayText }}</span>
      </span>
    </template>

    <template v-else-if="item[column.value]?.action">
      <MAction :title="item[column.value].title" :data="item[column.value].action" @action="$emit('action', $event)" />
    </template>

    <template v-else-if="isCheckboxColumn">
      <span class="checkbox-cell" :class="{ checked: checkboxValue }">
        <span class="checkbox-box">
          <i v-if="checkboxValue" class="pi pi-check" />
        </span>
      </span>
    </template>

    <template v-else-if="isIconItems">
      <div class="icon-items-container" :title="iconItemsTooltip">
        <template v-for="(iconItem, idx) in iconItems" :key="idx">
          <span class="icon-item">
            <img
              v-if="resolveIconItemUrl(iconItem.icon)"
              :src="resolveIconItemUrl(iconItem.icon)"
              :alt="iconItem.description"
              class="icon-image"
              @error="handleIconError($event, iconItem.icon)"
              @load="handleIconLoad"
            />
            <i v-else-if="getPrimeIconClass(iconItem.icon)"
              :class="[getPrimeIconClass(iconItem.icon), getIconColorClass(iconItem.icon)]" class="icon-class" />
            <span v-if="iconItem.description" class="icon-text">{{ iconItem.description }}</span>
          </span>
        </template>
      </div>
    </template>

    <template v-else-if="isHtmlColumn">
      <span v-if="sanitizedHtml" class="html-cell" v-html="sanitizedHtml"></span>
      <span v-else class="empty-cell">—</span>
    </template>

    <template v-else-if="isMultiLineTextColumn">
      <div
        v-if="rowsExpanded"
        class="multiline-cell multiline-cell--expanded"
        :title="undefined"
      >
        <span v-if="fullMultiLineText" class="multiline-cell-text multiline-cell-text--expanded">{{
          fullMultiLineText
        }}</span>
        <span v-else class="empty-cell">—</span>
      </div>
      <div v-else class="multiline-cell" :title="multiLineTooltip || undefined">
        <span v-if="collapsedMultiLineText" class="multiline-cell-text">{{ collapsedMultiLineText }}</span>
        <span v-else class="empty-cell">—</span>
        <button
          type="button"
          class="multiline-expand-button"
          :title="t('ui.expandCellText')"
          :aria-label="t('ui.expandCellText')"
          @click.stop="toggleMultiLinePopover"
          @dblclick.stop
        >
          <i class="pi pi-align-left" aria-hidden="true" />
        </button>
        <Popover ref="multiLinePopover" :dismissable="true" class="multiline-popover">
          <div class="multiline-popover-panel">
            <div class="multiline-popover-header">{{ multiLineDialogHeader }}</div>
            <pre class="multiline-dialog-body">{{ fullMultiLineText }}</pre>
          </div>
        </Popover>
      </div>
    </template>

    <template v-else>
      <span v-if="cellText !== null && cellText !== undefined">{{ cellText }}</span>
      <span v-else class="empty-cell">—</span>
    </template>
  </div>
</template>

<script setup lang="ts">
import MAction from '@/components/platform/MAction.vue';
import { usePageData } from '@/common/composables/usePageData';
import { useI18n } from '@/common/composables/useI18n';
import { resolveListboxCodeFromPageFields, resolveListboxImage } from '@/common/utils/listboxTableResolve';
import {
  isFalsyListboxCatalogCode,
  resolveVisibleListboxCellText,
  isWideListboxColumnWidth,
} from '@/common/utils/listboxTableCell';
import { normalizeActionImageName } from '@/services/action/normalizeActionTransfer';
import { readTableBooleanCellValue } from '@/common/utils/editableTableRowClick';
import { applyColumnFormat, isDateLikeTableColumnType, tableColumnTypeUsesTimeInDisplay, type IconItem } from './formatters';
import { getExplicitPrimeIcon, getPrimeIcon } from '@/common/utils/iconMapper';
import { resolvePlatformIcon, resolveSwingIconUrl } from '@/common/utils/resolvePlatformIcon';
import type { TreeTableRowSwingPresentation } from '@/common/utils/treeTableRowSwingPresentation';
import { parseTableColumnWidthPx } from '@/common/utils/parseTableColumnWidth';
import { computed, ref } from 'vue';
import Popover from 'primevue/popover';
import DOMPurify from 'dompurify';

interface Props {
  item: any
  column: any
  itemValue?: string
  /** Имя поля таблицы на форме (fieldName элемента TABLE) */
  tableFieldName?: string
  /** Стили строки / ячеек */
  rowPresentation?: TreeTableRowSwingPresentation
  /** Иконка узла (поле image) — только для первой колонки данных */
  leadingTreeIcon?: string
  /** Swing TableSizeManager.isExpanded — полный текст MultiLineText в ячейке */
  rowsExpanded?: boolean
}

const props = defineProps<Props>()
const emit = defineEmits<{
  action: [data: any]
}>()

const { pageFields } = usePageData()
const { t } = useI18n()

const cellStyle = computed(() => {
  const align = props.column?.align
  const base: Record<string, string> = align ? { textAlign: align as string } : {}
  const colKey = props.column?.value
  const p = props.rowPresentation
  if (!p || !colKey) return base
  const c = p.perColumn[colKey]
  if (!c) return base
  if (c.color) base.color = c.color
  if (c.backgroundColor) base.backgroundColor = c.backgroundColor
  if (c.fontWeight) base.fontWeight = c.fontWeight
  if (c.fontStyle) base.fontStyle = c.fontStyle
  if (c.padding) base.padding = c.padding
  return base
})

const leadingTreeIconResolution = computed(() => {
  const name = props.leadingTreeIcon
  if (!name) return null
  return resolvePlatformIcon(name)
})

const leadingTreeIconUrl = computed(() =>
  leadingTreeIconResolution.value?.kind === 'image' ? leadingTreeIconResolution.value.url : '',
)

const leadingTreeIconClass = computed(() => {
  const name = props.leadingTreeIcon
  if (!name || leadingTreeIconUrl.value) return ''
  return getPrimeIcon(name)
})

const resolveIconItemUrl = (iconName?: string): string | undefined => {
  if (!iconName) return undefined
  const resolution = resolvePlatformIcon(iconName)
  if (resolution.kind === 'image') return resolution.url
  return resolveSwingIconUrl(iconName)
}

const resolvePath = (obj: any, path: string) => {
  if (!obj || !path) return undefined
  if (Object.prototype.hasOwnProperty.call(obj, path)) return obj[path]
  const segments = path.split('.')
  return segments.reduce((acc: any, key: string) => (acc ? acc[key] : undefined), obj)
}

const resolveListboxRowFields = (item: any): Record<string, unknown> | null => {
  const row = item?._rowData ?? item?._originalRow
  if (!row || typeof row !== 'object') return null
  return (row as any).fields ?? row
}

const resolveColumnFormatString = (column: any): string => {
  const candidates = [
    column?.format,
    column?.format?.value,
    column?.format?._text,
    column?.formatName,
    column?.formatName?.value,
    column?.formatName?._text,
  ]
  for (const candidate of candidates) {
    if (typeof candidate === 'string' && candidate.trim()) {
      return candidate.trim()
    }
  }
  return ''
}

const usesSubTableListBoxCellValue = (column: any): boolean => {
  const format = resolveColumnFormatString(column)
  return /SubTableListBoxFormat=/i.test(format)
}

const cellRaw = computed(() => {
  const key = props.column?.value
  if (!key) return undefined

  const colType = normalizeColumnType(props.column?.type)
  const listLike =
    colType === 'LISTBOX' ||
    colType === 'LIST_BOX' ||
    colType === 'COMBOBOX' ||
    colType === 'COMBO' ||
    colType === 'SELECT'

  const wrapped = props.item?._rowData?.fields?.[key]
  if (usesSubTableListBoxCellValue(props.column) && wrapped != null) {
    return wrapped
  }
  if (listLike && wrapped != null && typeof wrapped === 'object') {
    const fromUnderscore =
      typeof wrapped._text === 'string' && wrapped._text.trim() !== '' ? wrapped._text : undefined
    const fromText =
      typeof wrapped.text === 'string' && wrapped.text.trim() !== '' ? wrapped.text : undefined
    const label = fromUnderscore ?? fromText
    if (label !== undefined) return label
  }

  if (listLike && props.item) {
    const suffixes = ['__label_data', '__text', '__description', '__name', '__caption']
    for (const suf of suffixes) {
      const altKey = `${key}${suf}`
      let alt = props.item[altKey]
      if (alt === undefined) alt = resolvePath(props.item, altKey)
      if (alt !== undefined && alt !== null && alt !== '') {
        const u = unwrapTypedValue(alt).value
        const scalar = u !== undefined ? u : alt
        if (typeof scalar === 'string' && scalar.trim()) return scalar
      }
    }
  }

  const direct = props.item?.[key]
  const raw = direct !== undefined ? direct : resolvePath(props.item, key)

  if (listLike && raw !== undefined && raw !== null && raw !== '') {
    const fromCatalog = resolveListboxCodeFromPageFields(pageFields.value, key, raw, {
      tableFieldName: props.tableFieldName,
      columnElementId: props.column?.columnElementId,
      rowFields: resolveListboxRowFields(props.item),
    })
    if (fromCatalog) return fromCatalog
  }

  return raw
})

const inlineActionCell = computed(() => {
  const raw = cellRaw.value
  return raw && typeof raw === 'object' && (raw as any).inlineAction ? raw as any : null
})

const isInlineActionCell = computed(() => !!inlineActionCell.value)

const inlineActionTitle = computed(() => {
  return inlineActionCell.value?.title || inlineActionCell.value?.action?.title?._text || ''
})

const inlineActionIconFile = computed(() => {
  const fromPayload = normalizeActionImageName(inlineActionCell.value?.icon)
  if (fromPayload) return fromPayload
  return normalizeActionImageName(inlineActionCell.value?.action?.image)
})

const inlineActionIconResolution = computed(() => {
  const name = inlineActionIconFile.value
  if (!name) return null
  return resolvePlatformIcon(name)
})

const inlineActionIconUrl = computed(() =>
  inlineActionIconResolution.value?.kind === 'image' ? inlineActionIconResolution.value.url : undefined,
)

const inlineActionIconClass = computed(() => {
  if (inlineActionIconUrl.value) return null
  const name = inlineActionIconFile.value
  if (!name) return null
  return getExplicitPrimeIcon(name) || null
})

const listboxColumnWidthPx = computed(() => parseTableColumnWidthPx(props.column?.width))

const listboxIconUsesLargeLayout = computed(
  () => isListLikeColumn.value && isWideListboxColumnWidth(listboxColumnWidthPx.value),
)

const listboxIconImageStyle = computed(() => {
  if (!listboxIconImageUrl.value || !listboxIconUsesLargeLayout.value) return undefined
  const columnWidth = listboxColumnWidthPx.value ?? 0
  const size = Math.round(Math.min(Math.max(columnWidth - 8, 64), 128))
  return {
    width: `${size}px`,
    height: `${size}px`,
  }
})

const listboxIconImageClass = computed(() =>
  listboxIconUsesLargeLayout.value ? 'listbox-column-icon' : 'icon-image',
)

const handleInlineActionIconError = (event: Event) => {
  handleIconError(event, inlineActionIconFile.value)
}

const handleInlineActionClick = () => {
  if (!inlineActionCell.value?.action) {
    return
  }

  emit('action', {
    action: inlineActionCell.value.action,
    rowData: props.item?._rowData || props.item,
    data: props.item,
    item: props.item,
    selectedIdx: props.item?._rowIndex,
    column: props.column?.value,
  })
}

const parseDateValue = (value: unknown): Date | null => {
  if (value === null || value === undefined || value === '') {
    return null
  }

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value
  }

  if (typeof value === 'number') {
    const date = new Date(value > 1000000000000 ? value : value * 1000)
    return Number.isNaN(date.getTime()) ? null : date
  }

  if (typeof value !== 'string') {
    return null
  }

  const trimmed = value.trim()
  if (!trimmed || trimmed === 'nullDateGMT' || trimmed === 'nullDateISO') {
    return null
  }

  const direct = new Date(trimmed)
  if (!Number.isNaN(direct.getTime())) {
    return direct
  }

  const ruDateTimeMatch = trimmed.match(
    /^(\d{2})\.(\d{2})\.(\d{2,4})(?:\s+(\d{2}):(\d{2})(?::(\d{2}))?)?$/,
  )
  if (ruDateTimeMatch) {
    const [, day, month, year, hours = '00', minutes = '00', seconds = '00'] = ruDateTimeMatch
    const normalizedYear = year.length === 2 ? `20${year}` : year
    const parsed = new Date(
      Number(normalizedYear),
      Number(month) - 1,
      Number(day),
      Number(hours),
      Number(minutes),
      Number(seconds),
    )
    return Number.isNaN(parsed.getTime()) ? null : parsed
  }

  return null
}

const formatDate = (value: unknown, withTime: boolean) => {
  if (value === null || value === undefined || value === '') return value
  const d = parseDateValue(value)
  if (!d) {
    return value
  }
  const pad = (n: number) => (n < 10 ? `0${n}` : `${n}`)
  const datePart = `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()}`
  if (!withTime) return datePart
  return `${datePart} ${pad(d.getHours())}:${pad(d.getMinutes())}`
}

const unwrapTypedValue = (value: any): { type?: string; value: any } => {
  if (value === null || value === undefined) {
    return { value }
  }

  if (typeof value === 'object' && 'value' in (value as any) && (value as any).value !== undefined) {
    return { type: (value as any).type, value: (value as any).value }
  }

  return { value }
}

const normalizeColumnType = (type: any): string => {
  if (!type) return ''
  if (typeof type === 'string') return type.toUpperCase()
  if (typeof type === 'object') {
    if (typeof type.value === 'string') return type.value.toUpperCase()
    if (typeof type._text === 'string') return type._text.toUpperCase()
  }
  return String(type).toUpperCase()
}

const isListLikeColumnType = (columnType: string): boolean =>
  columnType === 'LISTBOX' ||
  columnType === 'LIST_BOX' ||
  columnType === 'COMBOBOX' ||
  columnType === 'COMBO' ||
  columnType === 'SELECT'

const isListLikeColumn = computed(() => isListLikeColumnType(normalizeColumnType(props.column?.type)))

const resolveBackingFieldType = (): string | undefined => {
  const key = props.column?.value
  if (!key) return undefined
  const field = props.item?._rowData?.fields?.[key]
  if (field != null && typeof field === 'object' && typeof (field as any).type === 'string') {
    return (field as any).type
  }
  return undefined
}

const resolveDateDisplayMode = (
  column: any,
  typedType?: string,
  backingFieldType?: string,
): { isDateLike: boolean; withTime: boolean } => {
  const columnType = normalizeColumnType(column?.type)
  const typed = typedType ? normalizeColumnType(typedType) : ''
  const backing = backingFieldType ? normalizeColumnType(backingFieldType) : ''

  const typeForMode = isDateLikeTableColumnType(columnType)
    ? columnType
    : isDateLikeTableColumnType(backing)
      ? backing
      : isDateLikeTableColumnType(typed)
        ? typed
        : ''

  if (!typeForMode) {
    return { isDateLike: false, withTime: false }
  }

  return {
    isDateLike: true,
    withTime: tableColumnTypeUsesTimeInDisplay(typeForMode),
  }
}

const applyDefaultFormatting = (raw: any, column: any, backingFieldType?: string): any => {
  if (raw === null || raw === undefined) {
    return raw
  }

  const { type: typedType, value: typedValue } = unwrapTypedValue(raw)
  const value = typedValue !== undefined ? typedValue : raw

  const { isDateLike, withTime } = resolveDateDisplayMode(column, typedType, backingFieldType)

  if (typeof value === 'string' && isDateLike) {
    return formatDate(value, withTime)
  }

  if (typeof value === 'number' && isDateLike) {
    const date = new Date(value)
    if (!Number.isNaN(date.getTime())) {
      return formatDate(date.toISOString(), withTime)
    }
  }

  if (typeof value === 'object') {
    if (Array.isArray(value)) {
      return value.join(', ')
    }

    try {
      return JSON.stringify(value)
    } catch {
      return String(value)
    }
  }

  return value
}

const isCheckboxColumn = computed(() => {
  const columnType = normalizeColumnType(props.column?.type)
  if (isListLikeColumnType(columnType)) {
    return false
  }
  if (columnType === 'CHECKBOX' || columnType === 'BOOLEAN') {
    return true
  }

  const { type: valueType, value } = unwrapTypedValue(cellRaw.value)
  const normalizedValueType = normalizeColumnType(valueType)

  if (normalizedValueType === 'BOOLEAN') {
    return true
  }

  if (typeof value === 'boolean' || typeof cellRaw.value === 'boolean') {
    return true
  }

  return false
})

const listboxRawCode = computed(() => {
  const key = props.column?.value
  if (!key) return undefined

  const direct = props.item?.[key]
  let raw = direct !== undefined ? direct : resolvePath(props.item, key)

  if (raw === undefined) {
    const wrapped = props.item?._rowData?.fields?.[key]
    if (wrapped != null) {
      const { value } = unwrapTypedValue(wrapped)
      raw = value !== undefined ? value : wrapped
    }
  }

  const { value } = unwrapTypedValue(raw)
  return value !== undefined ? value : raw
})

const listboxDisplayText = computed(() => {
  if (!isListLikeColumn.value) return ''
  const text = cellRaw.value
  if (text === null || text === undefined || text === '') return ''
  if (typeof text === 'object') return ''
  return String(text)
})

const listboxRowFields = computed(() => {
  return resolveListboxRowFields(props.item)
})

const listboxResolveOptions = computed(() => ({
  tableFieldName: props.tableFieldName,
  columnElementId: props.column?.columnElementId,
  rowFields: listboxRowFields.value,
}))

const listboxIconImage = computed(() => {
  if (!isListLikeColumn.value) return undefined
  const code = listboxRawCode.value
  if (code === undefined || code === null || code === '') return undefined
  return resolveListboxImage(pageFields.value, props.column?.value, code, {
    ...listboxResolveOptions.value,
    catalogRows: props.column?.listboxCatalog,
  })
})

const listboxIconImageUrl = computed(() => {
  const file = listboxIconImage.value
  if (!file) return undefined
  const swingUrl = resolveSwingIconUrl(file)
  if (swingUrl) return swingUrl
  const resolution = resolvePlatformIcon(file)
  return resolution.kind === 'image' ? resolution.url : undefined
})

const visibleListboxDisplayText = computed(() => {
  if (!isListLikeColumn.value) return ''
  return resolveVisibleListboxCellText(
    listboxRawCode.value,
    listboxDisplayText.value,
    Boolean(listboxIconImageUrl.value),
  )
})

const listboxIconTooltip = computed(() => {
  if (!isListLikeColumn.value) return ''
  const code = listboxRawCode.value
  if (code === undefined || code === null || code === '') return ''
  if (isFalsyListboxCatalogCode(code)) return ''
  return (
    resolveListboxCodeFromPageFields(pageFields.value, props.column?.value, code, listboxResolveOptions.value) ||
    ''
  )
})

const isListboxIconCell = computed(() => isListLikeColumn.value)

const checkboxValue = computed(() => {
  if (!isCheckboxColumn.value) return false
  return readTableBooleanCellValue(cellRaw.value)
})

// Decode HTML entities in text
const decodeHtmlEntities = (text: string): string => {
  if (typeof text !== 'string') return text

  const div = document.createElement('div')
  div.innerHTML = text
  return div.textContent || div.innerText || text
}

const extractHtmlBody = (html: string): string => {
  if (!html || typeof html !== 'string') return html

  const trimmed = html.trim()

  const lowerTrimmed = trimmed.toLowerCase()
  if (lowerTrimmed.startsWith('<html')) {
    const bodyMatch = trimmed.match(/<body[^>]*>\s*(.*?)\s*<\/body>/is)
    if (bodyMatch && bodyMatch[1]) {
      return bodyMatch[1].trim()
    }

    const htmlMatch = trimmed.match(/<html[^>]*>\s*(.*?)\s*<\/html>/is)
    if (htmlMatch && htmlMatch[1]) {
      let content = htmlMatch[1].trim()
      content = content.replace(/<head[^>]*>.*?<\/head>/is, '')
      return content.trim()
    }
  }

  return trimmed
}

const formattedValue = computed(() => {
  const rawValue = cellRaw.value
  if (rawValue === null || rawValue === undefined) return { type: 'empty', value: rawValue }

  const formatted = applyColumnFormat(rawValue, props.column, {
    pageFields: pageFields.value,
    tableFieldName: props.tableFieldName,
    columnFieldName: props.column?.value,
    columnElementId: props.column?.columnElementId,
  })

  if (formatted.applied) {
    // Check if it's icon items
    if (formatted.value && typeof formatted.value === 'object' && (formatted.value as any).__iconItems) {
      return { type: 'iconItems', value: (formatted.value as any).__iconItems as IconItem[] }
    }
    return { type: 'text', value: formatted.value }
  } else {
    const result = applyDefaultFormatting(rawValue, props.column, resolveBackingFieldType())
    // Decode HTML entities if result is a string
    if (typeof result === 'string') {
      return { type: 'text', value: decodeHtmlEntities(result) }
    }
    return { type: 'text', value: result }
  }
})

const isIconItems = computed(() => {
  return formattedValue.value.type === 'iconItems'
})

const iconItems = computed(() => {
  if (formattedValue.value.type === 'iconItems') {
    return formattedValue.value.value as IconItem[]
  }
  return []
})

const iconItemsTooltip = computed(() => {
  if (!isIconItems.value) return ''
  return iconItems.value
    .map(item => item.tooltip || item.description)
    .filter(Boolean)
    .join('; ')
})

const cellText = computed(() => {
  if (isCheckboxColumn.value) return null
  if (formattedValue.value.type === 'text') {
    return formattedValue.value.value
  }
  return null
})

const isHtmlColumn = computed(() => {
  const columnType = normalizeColumnType(props.column?.type)
  return columnType === 'HTML'
})

const isMultiLineTextColumn = computed(() => {
  return normalizeColumnType(props.column?.type) === 'MULTILINETEXT'
})

const multiLinePopover = ref<{ toggle: (event: Event) => void } | null>(null)

const fullMultiLineText = computed(() => {
  const raw = cellRaw.value
  if (raw === null || raw === undefined) return ''
  const { value } = unwrapTypedValue(raw)
  const resolved = value !== undefined ? value : raw
  if (typeof resolved === 'string') return resolved
  if (resolved === null || resolved === undefined) return ''
  if (Array.isArray(resolved)) return resolved.map((part) => String(part ?? '')).join('\n')
  return String(resolved)
})

const collapsedMultiLineText = computed(() => {
  const text = fullMultiLineText.value
  if (!text) return ''
  if (text.toLowerCase().startsWith('<html>')) return text
  const newlineIndex = text.indexOf('\n')
  return newlineIndex > -1 ? text.substring(0, newlineIndex) : text
})

const multiLineTooltip = computed(() => {
  const text = fullMultiLineText.value
  if (!text) return ''
  return text.includes('\n') ? text : ''
})

const multiLineDialogHeader = computed(() => {
  const name = props.column?.name
  if (typeof name === 'string' && name.trim()) return name.trim()
  return t('ui.expandCellText')
})

const toggleMultiLinePopover = (event: Event) => {
  multiLinePopover.value?.toggle(event)
}

const sanitizedHtml = computed(() => {
  if (!isHtmlColumn.value) return ''

  const rawValue = cellRaw.value
  if (rawValue === null || rawValue === undefined) return ''

  const { value: typedValue } = unwrapTypedValue(rawValue)
  const value = typedValue !== undefined ? typedValue : rawValue

  const htmlString = typeof value === 'string' ? value : String(value)
  if (!htmlString) return ''

  const isEscaped = htmlString.includes('&lt;') || htmlString.includes('&gt;')

  let processedHtml = htmlString

  if (isEscaped) {
    processedHtml = htmlString
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      .replace(/&#x27;/g, "'")
      .replace(/&#x2F;/g, '/')
  }

  const extractedContent = extractHtmlBody(processedHtml)

  const config = {
    ALLOWED_TAGS: [
      'p', 'br', 'strong', 'em', 'u', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
      'ul', 'ol', 'li', 'a', 'span', 'div', 'table', 'tr', 'td', 'th',
      'thead', 'tbody', 'img', 'blockquote', 'code', 'pre'
    ],
    ALLOWED_ATTR: ['href', 'target', 'src', 'alt', 'title', 'class', 'style'],
    ALLOWED_DATA_ATTR: false
  }

  return DOMPurify.sanitize(extractedContent, config)
})

const getPrimeIconClass = (iconName?: string): string | null => {
  if (!iconName) return null

  const nameWithoutExt = iconName.replace(/\.(png|gif|jpg|jpeg|svg)$/i, '')
  return getPrimeIcon(nameWithoutExt)
}

const getIconColorClass = (iconName?: string): string | null => {
  if (!iconName) return null

  const nameWithoutExt = iconName.replace(/\.(png|gif|jpg|jpeg|svg)$/i, '')

  // Add color class for status indicators
  if (nameWithoutExt.includes('red-du-')) return 'red-du-small'
  if (nameWithoutExt.includes('green-du-')) return 'green-du-small'
  if (nameWithoutExt.includes('yellow-du-')) return 'yellow-du-small'
  if (nameWithoutExt.includes('grey-du-')) return 'grey-du-small'
  if (nameWithoutExt.includes('bullet_purple')) return 'bullet_purple'
  if (nameWithoutExt.includes('bullet_blue')) return 'bullet_blue'
  if (nameWithoutExt.includes('bullet_red')) return 'bullet_red'
  if (nameWithoutExt.includes('bullet_black')) return 'bullet_black'

  return null
}

const handleIconLoad = (event: Event) => {
  const img = event.target as HTMLImageElement
  img.style.display = ''
}

const handleIconError = (event: Event, iconName?: string) => {
  const img = event.target as HTMLImageElement
  let extractedName = iconName || img.src.split('/').pop()?.replace(/\.(png|gif|jpg|jpeg|svg)$/i, '')

  if (extractedName && iconName) {
    extractedName = extractedName.replace(/\.(png|gif|jpg|jpeg|svg)$/i, '')
  }

  if (extractedName) {
    const iconClass = getPrimeIcon(extractedName)
    if (iconClass && iconClass !== 'pi pi-question-circle') {
      const parent = img.parentElement
      if (parent) {
        const iconEl = document.createElement('i')
        const colorClass = getIconColorClass(extractedName)
        iconEl.className = `${iconClass} icon-class${colorClass ? ` ${colorClass}` : ''}`
        img.replaceWith(iconEl)
      }
    } else {
      img.style.display = 'none'
    }
  } else {
    // Hide broken image if we can't extract name
    img.style.display = 'none'
  }
}

</script>

<style scoped>
.cell-content {
  display: flex;
  align-items: center;
  min-height: 24px;
  width: 100%;
  min-width: 0;
  max-width: 100%;
}

.multiline-cell {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  min-width: 0;
  width: 100%;
  max-width: 100%;
}

.multiline-cell-text {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.multiline-cell--expanded {
  align-items: flex-start;
}

.multiline-cell-text--expanded {
  overflow: visible;
  text-overflow: unset;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.multiline-expand-button {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.25rem;
  height: 1.25rem;
  padding: 0;
  border: none;
  border-radius: 3px;
  background: transparent;
  color: #475569;
  cursor: pointer;
}

.multiline-expand-button:hover {
  background: rgba(100, 116, 139, 0.12);
  color: #0f172a;
}

.multiline-expand-button .pi {
  font-size: 0.8rem;
}

.multiline-popover-panel {
  width: min(36rem, 92vw);
  max-width: 92vw;
}

.multiline-popover-header {
  margin-bottom: 0.5rem;
  font-weight: 600;
  font-size: 0.95rem;
}

.multiline-dialog-body {
  margin: 0;
  min-height: 200px;
  max-height: min(50vh, 320px);
  overflow: auto;
  white-space: pre-wrap;
  word-break: break-word;
  font: inherit;
  font-family: inherit;
}

.cell-content-inline-action {
  width: 100%;
}

.inline-action-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.375rem;
  width: 100%;
  min-height: 36px;
  padding: 0.5rem 0.875rem;
  border: 1px solid #cbd5e1;
  border-radius: 10px;
  background:
    linear-gradient(180deg, rgba(255, 255, 255, 0.98) 0%, rgba(241, 245, 249, 0.96) 100%);
  color: inherit;
  cursor: pointer;
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.95),
    0 1px 2px rgba(15, 23, 42, 0.08);
  transition:
    background-color 0.15s ease,
    border-color 0.15s ease,
    box-shadow 0.15s ease,
    transform 0.08s ease;
}

.inline-action-button:hover {
  border-color: #94a3b8;
  background:
    linear-gradient(180deg, rgba(255, 255, 255, 1) 0%, rgba(226, 232, 240, 0.96) 100%);
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.98),
    0 2px 6px rgba(15, 23, 42, 0.12);
}

.inline-action-button:active {
  transform: translateY(1px);
  box-shadow:
    inset 0 1px 2px rgba(15, 23, 42, 0.12),
    0 1px 2px rgba(15, 23, 42, 0.08);
}

.inline-action-label {
  flex: 1;
  line-height: 1.2;
  text-align: center;
  color: inherit;
}

.inline-action-icon {
  font-size: 0.875rem;
  color: inherit;
}

.inline-action-icon-img {
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  object-fit: contain;
}

.tree-row-leading-icon {
  margin-right: 0.35rem;
  flex-shrink: 0;
  font-size: 0.875rem;
}

.tree-row-leading-icon-image {
  width: 1rem;
  height: 1rem;
  object-fit: contain;
}

.checkbox-cell {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  color: #64748b;
}

.checkbox-box {
  width: 1rem;
  height: 1rem;
  border: 1.5px solid rgba(100, 116, 139, 0.6);
  border-radius: 4px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(100, 116, 139, 0.08);
  transition: all 0.15s ease;
}

.checkbox-cell.checked {
  color: var(--primary-color, #1e3a6e);
}

.checkbox-cell.checked .checkbox-box {
  border-color: var(--primary-color, #1e3a6e);
  background-color: var(--primary-color, #1e3a6e);
  color: #ffffff;
}

.checkbox-cell.checked .checkbox-box i {
  font-size: 0.75rem;
}

.empty-cell {
  color: #9ca3af;
  font-style: italic;
}

.html-cell {
  display: block;
  word-wrap: break-word;
}

.icon-items-container {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.icon-item {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  white-space: nowrap;
}

.listbox-icon-cell {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
}

.listbox-icon-cell--large {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: 96px;
}

.listbox-column-icon {
  object-fit: contain;
  flex-shrink: 0;
}

.listbox-cell-text {
  font-size: inherit;
  line-height: 1.2;
}

.icon-image {
  width: 16px;
  height: 16px;
  object-fit: contain;
  flex-shrink: 0;
}

.icon-class {
  font-size: 0.875rem;
  color: inherit;
}

.icon-text {
  font-size: 0.875rem;
  line-height: 1.2;
}

.icon-class.pi-circle-fill.red-du-small,
.icon-class.pi-circle-fill.red-du-medium,
.icon-class.pi-circle-fill.red-du-big {
  color: #ef4444;
}

.icon-class.pi-circle-fill.green-du-small,
.icon-class.pi-circle-fill.green-du-medium,
.icon-class.pi-circle-fill.green-du-big {
  color: #22c55e;
}

.icon-class.pi-circle-fill.yellow-du-small,
.icon-class.pi-circle-fill.yellow-du-medium,
.icon-class.pi-circle-fill.yellow-du-big {
  color: #eab308;
}

.icon-class.pi-circle-fill.grey-du-small,
.icon-class.pi-circle-fill.grey-du-medium,
.icon-class.pi-circle-fill.grey-du-big {
  color: #6b7280;
}

:deep(.icon-class.pi-circle-fill.bullet_purple) {
  color: #804dff;
}

:deep(.icon-class.pi-circle-fill.bullet_blue) {
  color: #23b4ff;
}

:deep(.icon-class.pi-circle-fill.bullet_red) {
  color: #ff0000;
}

:deep(.icon-class.pi-circle-fill.bullet_black) {
  color: #000000;
}
</style>
